import { useState, type ReactNode } from 'react';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import styles from '../App.module.css';

interface ExpandableTextProps {
  text: string;
  /** この文字数を超えたら折りたたむ（デフォルト 70） */
  limit?: number;
  className?: string;
}

/** 長い本文を短く見せ、「もっと見る / 閉じる」で開閉する */
export function ExpandableText({ text, limit = 70, className }: ExpandableTextProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [open, setOpen] = useState(false);
  const needsClamp = text.length > limit;

  if (!needsClamp) {
    return <p className={className}>{text}</p>;
  }

  const preview = text.slice(0, limit).replace(/\s+$/, '') + '…';

  return (
    <div className={styles.expandableBlock}>
      <p className={className}>{open ? text : preview}</p>
      <button
        type="button"
        className={styles.textToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? ui.expand.less : ui.expand.more}
      </button>
    </div>
  );
}

interface DetailDisclosureProps {
  detail: string;
  className?: string;
}

/** 短い説明の下に「詳しく」で詳細を開く */
export function DetailDisclosure({ detail, className }: DetailDisclosureProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [open, setOpen] = useState(false);
  if (!detail.trim()) return null;

  return (
    <div className={`${styles.detailDisclosure} ${className ?? ''}`.trim()}>
      <button
        type="button"
        className={styles.textToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? ui.expand.hideDetail : ui.expand.detail}
      </button>
      {open ? <p className={styles.detailBody}>{detail}</p> : null}
    </div>
  );
}

interface CollapsibleItemsProps {
  total: number;
  initial?: number;
  children: (visibleCount: number) => ReactNode;
}

/** 一覧の件数が多いとき、最初の数件だけ見せ「もっと見る」 */
export function CollapsibleItems({ total, initial = 3, children }: CollapsibleItemsProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [expanded, setExpanded] = useState(false);
  const visible = expanded || total <= initial ? total : initial;
  const hidden = Math.max(0, total - visible);

  return (
    <div className={styles.collapsibleItems}>
      {children(visible)}
      {total > initial ? (
        <button
          type="button"
          className={styles.moreItemsButton}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? ui.expand.lessItems
            : ui.expand.moreItems.replace('{count}', String(hidden))}
        </button>
      ) : null}
    </div>
  );
}
